import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { useLang, type Key } from '../i18n'
import { isApiError } from '../lib/api'
import type { FlattenCounts } from '../lib/types'

/**
 * "Flatten history" (AB#243): names the version kept as the new start of the history (the row the
 * member chose in the History modal, the newest by default), reads the dry-run count of what goes,
 * then confirms. Every version older than the kept one is deleted for good, tagged and
 * named ones aside — so the counts are shown BEFORE "Confirm flatten" can be pressed, the warning
 * says it cannot be undone, and Cancel (or Escape, or the backdrop) sends nothing destructive.
 * A refusal (409 `room_busy`, 403, …) is said in words inside the dialog, which stays open.
 * Focus starts on Cancel and is trapped inside; the parent puts it back on its trigger.
 */
export function FlattenHistoryDialog({
  keep,
  keepName,
  newest,
  onPreview,
  onConfirm,
  onClose,
  onDone,
}: {
  /** The version kept: it and every newer one survive. */
  keep: number
  /** How it is named to the member: "version 54 — 13 Oct 10:51, Sofia Khan". */
  keepName: string
  /** It is the room's newest version (no row was chosen, or the newest was). */
  newest: boolean
  /** The dry run: what flattening down to `keep` would remove. */
  onPreview: (keep: number) => Promise<FlattenCounts>
  /** The flatten itself. Rejects when the server refused. */
  onConfirm: (keep: number) => Promise<unknown>
  /** Cancel: nothing was deleted. */
  onClose: () => void
  /** The flatten succeeded. */
  onDone: () => void
}) {
  const { t } = useLang()
  const titleId = useId()
  const warnId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const [counts, setCounts] = useState<FlattenCounts | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [working, setWorking] = useState(false)

  useEffect(() => {
    cancelRef.current?.focus()
  }, [])

  /** A refusal in words: a held lock, a missing right, anything else. */
  const reason = (e: unknown, fallback: Key): string => {
    if (isApiError(e) && e.code === 'room_busy') return t('hist_flatten_room_busy')
    if (isApiError(e) && e.status === 403) return t('hist_flatten_forbidden')
    return t(fallback)
  }

  // The dry run. A stale answer never overwrites a newer one.
  useEffect(() => {
    let live = true
    setCounts(null)
    setError(null)
    onPreview(keep)
      .then((c) => live && setCounts(c))
      .catch((e: unknown) => live && setError(reason(e, 'hist_flatten_count_failed')))
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `reason` only reads `t`
  }, [keep, onPreview])

  const confirm = (): void => {
    if (working) return
    setWorking(true)
    setError(null)
    onConfirm(keep)
      .then(() => onDone())
      .catch((e: unknown) => {
        setWorking(false)
        setError(reason(e, 'hist_flatten_failed'))
      })
  }

  const onKeyDown = (e: ReactKeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      onClose()
      return
    }
    if (e.key !== 'Tab') return
    const focusables = dialogRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled)')
    if (!focusables || focusables.length === 0) return
    const first = focusables[0]!
    const last = focusables[focusables.length - 1]!
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  const plural = (n: number, one: Key, many: Key): string => (n === 1 ? t(one) : t(many).replace('{n}', String(n)))
  const nothing = counts !== null && counts.removed === 0

  return createPortal(
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        e.stopPropagation()
        if (!working) onClose()
      }}
    >
      <div
        className="modal confirm-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={warnId}
        ref={dialogRef}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="modal-head">
          <h2 id={titleId}>{t('hist_flatten_title')}</h2>
          <button className="modal-close" aria-label={t('close')} onClick={onClose} disabled={working}>
            ✕
          </button>
        </div>
        <div className="modal-body prompt-body">
          <p className="flatten-keep">
            {t('hist_flatten_keep').replace('{version}', keepName)}
            {newest && ` ${t('hist_flatten_newest')}`}
          </p>
          <p className="flatten-counts" role="status" aria-live="polite">
            {counts === null
              ? !error && t('hist_flatten_counting')
              : nothing
                ? t('hist_flatten_nothing')
                : `${plural(counts.removed, 'hist_flatten_removed_one', 'hist_flatten_removed')}, ${plural(
                    counts.keptProtected,
                    'hist_flatten_kept_one',
                    'hist_flatten_kept',
                  )}.`}
          </p>
          <p id={warnId} className="flatten-warning">{t('hist_flatten_warning')}</p>
          {error && (
            <p className="prompt-error" role="alert">
              {error}
            </p>
          )}
          <div className="prompt-acts">
            <button ref={cancelRef} className="mini ghost" onClick={onClose} disabled={working}>
              {t('cancel')}
            </button>
            <button
              className="mini danger"
              disabled={counts === null || nothing || working}
              aria-busy={working}
              onClick={confirm}
            >
              {working ? t('hist_flatten_working') : t('hist_flatten_confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
